// Centralized real-time listener registry.
// Replaces the legacy setupRealtimeListeners/setupHeroListeners pattern.

import { fb } from './firebase.js';
import { state } from './state.js';
import {
  metaRef, heroesCol, habitsCol, rewardsCol, approvalsCol, maestrosCol,
  calendarCol, alertsCol, maestroNotifCol,
  heroRef, dailyRef, historyCol, notificationsCol,
} from './paths.js';
import { getWeekDates, getTodayStr } from '../utils/date.js';

const { onSnapshot, query, orderBy, limit } = fb;

let familyUnsubs = [];
let heroUnsubs = [];

const FREE_SUBSCRIPTION = { tier: 'free', status: 'active' };

// El webhook de Wompi escribe la suscripción en families/{fid}.subscription;
// aquí se propaga a state.subscription, que es lo que lee isPro().
export function applyMetaData(data) {
  state.meta = data;
  state.subscription = data.subscription || FREE_SUBSCRIPTION;
}

export function startFamilyListeners() {
  stopFamilyListeners();
  state.syncStatus = 'syncing';

  familyUnsubs.push(onSnapshot(metaRef(), s => { if (s.exists()) applyMetaData(s.data()); state.syncStatus = 'idle'; }));
  familyUnsubs.push(onSnapshot(heroesCol(), s => { state.heroes = s.docs.map(d => ({ id: d.id, ...d.data() })); }));
  familyUnsubs.push(onSnapshot(habitsCol(), s => { state.habits = s.docs.map(d => ({ id: d.id, ...d.data() })); }));
  familyUnsubs.push(onSnapshot(rewardsCol(), s => { state.rewards = s.docs.map(d => ({ id: d.id, ...d.data() })); }));
  familyUnsubs.push(onSnapshot(query(approvalsCol(), orderBy('createdAt', 'desc'), limit(50)),
    s => { state.approvals = s.docs.map(d => ({ id: d.id, ...d.data() })); }));
  familyUnsubs.push(onSnapshot(maestrosCol(), s => { state.maestros = s.docs.map(d => ({ id: d.id, ...d.data() })); }));
  familyUnsubs.push(onSnapshot(calendarCol(), s => { state.calendarEvents = s.docs.map(d => ({ id: d.id, ...d.data() })); }));
  familyUnsubs.push(onSnapshot(query(alertsCol(), orderBy('ts', 'desc'), limit(30)),
    s => { state.alertLogs = s.docs.map(d => ({ id: d.id, ...d.data() })); }));
  familyUnsubs.push(onSnapshot(query(maestroNotifCol(), orderBy('ts', 'desc'), limit(50)),
    s => { state.maestroNotifications = s.docs.map(d => ({ id: d.id, ...d.data() })); }));
}

export function stopFamilyListeners() {
  familyUnsubs.forEach(u => { try { u(); } catch {} });
  familyUnsubs = [];
  state.subscription = FREE_SUBSCRIPTION;
}

export function startHeroListeners(heroId) {
  stopHeroListeners();
  if (!heroId) return;

  heroUnsubs.push(onSnapshot(heroRef(heroId), s => {
    if (s.exists()) {
      const idx = state.heroes.findIndex(h => h.id === heroId);
      const updated = { id: heroId, ...s.data() };
      const next = [...state.heroes];
      if (idx >= 0) next[idx] = updated; else next.push(updated);
      state.heroes = next;
    }
  }));

  const today = getTodayStr();
  const days = getWeekDates();

  for (const d of days) {
    heroUnsubs.push(onSnapshot(dailyRef(d, heroId), s => {
      state.daily = { ...state.daily, [d]: s.exists() ? s.data() : {} };
    }));
  }

  heroUnsubs.push(onSnapshot(
    query(historyCol(heroId), orderBy('ts', 'desc'), limit(20)),
    s => { state.history = s.docs.map(d => ({ id: d.id, ...d.data() })); }
  ));

  heroUnsubs.push(onSnapshot(
    query(notificationsCol(heroId), orderBy('ts', 'desc'), limit(30)),
    s => { state.notifications = s.docs.map(d => ({ id: d.id, ...d.data() })); }
  ));
}

export function stopHeroListeners() {
  heroUnsubs.forEach(u => { try { u(); } catch {} });
  heroUnsubs = [];
}

export function stopAllListeners() {
  stopFamilyListeners();
  stopHeroListeners();
}
